import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Row, Col, Form, Tabs, Tab } from 'react-bootstrap';
import { get } from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import moment from 'moment';

import Loading from '../../components/Loading';
import axios from '../../services/axios';
import Breadcrumbs from '../../components/Breadcrumbs';
import { useNotification } from '../../components/Notification/NotificationContext';
import Forms from '../../components/Forms';
import ErrorProcessor from '../../components/ErrorProcessor';
import CustomImage from '../../components/CustomImage';
import CustomerAddr from './CustomerAddr';
import CustomerPay from './CustomerPay';
import CustomerContract from './CustomerContract';
import ButtonModal from '../../components/ModalConfirmation/ButtonModal';
import CustomerPayMethod from './CustomerPayMethod';

export default function Customer() {
  const [activeTab, setActiveTab] = useState('main');
  const routeName = '/customers';
  const dataName = 'Cliente';
  const titleName = 'Clientes';
  const [dataLoaded, setDataLoaded] = useState(false);
  const [apiErrors, setApiErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const notification = useNotification();
  const navigate = useNavigate();
  const params = useParams();
  const [id, setId] = useState(get(params, 'id', ''));
  const [companies, setCompanies] = useState([]);
  const [customerUpdated, setCustomerUpdated] = useState(false);
  const [fieldsKey, setFieldsKey] = useState(0); // Para forçar re-render dos campos
  const [formEdit, setFormEdit] = useState({
    name: '',
    lastname: '',
    notes: '',
    cpf_cnpj: '',
    guardian_name: '',
  });
  // Valor exibido no input de data (DD/MM/YYYY). Mantemos a data interna em formEdit.dob no formato YYYY-MM-DD
  const [displayDob, setDisplayDob] = useState('');

  // Função para verificar se é menor de idade
  const isMinor = (dateOfBirth) => {
    if (!dateOfBirth) return false;
    const today = moment();
    const birthDate = moment(dateOfBirth);
    const age = today.diff(birthDate, 'years');
    return age < 18;
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .min(3, 'Nome deve conter no mínimo 3 caracteres.')
      .required('Nome obrigatório.'),
    lastname: Yup.string()
      .min(3, 'Sobrenome deve conter no mínimo 3 caracteres.')
      .nullable(),
    company_id: Yup.number()
      .typeError('Deve ser numérico')
      .required('Empresa obrigatória.'),
    type_person: Yup.string().required('Tipo de pessoa obrigatória.'),

    cpf_cnpj: Yup.string()
      .test(
        'len',
        'CPF ou CNPJ inválido.',
        (value) => !value || value.replace(/[^\d]/g, '').length >= 11
      ),

    email: Yup.string().email('E-mail inválido.').nullable(),
    landline_phone: Yup.number()
      .typeError('Deve ser numérico')
      .test(
        'len',
        'Número telefônico inválido, formato correto DDD + Número',
        (value) => !value || value.toString().length >= 10
      )
      .nullable(),
    business_phone: Yup.number()
      .typeError('Deve ser numérico')
      .test(
        'len',
        'Número telefônico inválido, formato correto DDD + Número',
        (value) => !value || value.toString().length >= 10
      )
      .nullable(),
    mobile_phone: Yup.number()
      .typeError('Deve ser numérico')
      .test(
        'len',
        'Número telefônico inválido, formato correto DDD + Número',
        (value) => !value || value.toString().length >= 10
      )
      .nullable(),
    additional_phone: Yup.number()
      .typeError('Deve ser numérico')
      .test(
        'len',
        'Número telefônico inválido, formato correto DDD + Número',
        (value) => !value || value.toString().length >= 10
      )
      .nullable(),
    dob: Yup.string()
      .test('is-valid-date', 'Deve ser uma data válida no formato DD/MM/AAAA', function (value) {
        if (!value) return true; // Campo opcional

        // Se o valor tem formato DD/MM/YYYY, valida
        if (value.length === 10 && value.includes('/')) {
          const [day, month, year] = value.split('/');
          return moment(`${year}-${month}-${day}`, 'YYYY-MM-DD', true).isValid();
        }

        // Se o valor tem formato YYYY-MM-DD, valida
        return moment(value, 'YYYY-MM-DD', true).isValid();
      })
      .nullable(),
    guardian_name: Yup.string()
      .when('dob', (dob, schema) => {
        if (dob && isMinor(dob)) {
          return schema
            .min(3, 'Nome do responsável deve conter no mínimo 3 caracteres.')
            .required('Nome do responsável é obrigatório para menores de idade.');
        }
        return schema.nullable();
      }),
  });

  function formatCpfCnpjLive(raw, type) {
    console.log("type", type)
    console.log("digits", raw)

    const digits = raw.replace(/\D/g, '');
    let result = digits;

    if (type === 'fisica') {
      result = digits
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
        .slice(0, 14);
    } else if (type === 'juridica') {
      result = digits
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2')
        .slice(0, 18);
    }
    console.log("result", result)

    return result;
  }
  const handleFieldChange = useCallback((fieldName, value) => {
    let newValue = false;

    if (fieldName === 'dob') {
      // Se o valor está vazio, manter como vazio
      if (!value) {
        newValue = '';
      } else {
        // Tentar validar a data no formato YYYY-MM-DD (formato padrão)
        let validateDate = moment(value, 'YYYY-MM-DD', true);

        // Se não for válido, tentar formato brasileiro DD/MM/YYYY
        if (!validateDate.isValid()) {
          validateDate = moment(value, 'DD/MM/YYYY', true);
        }

        newValue = validateDate.isValid()
          ? validateDate.format('YYYY-MM-DD')
          : ''; // Deixar vazio se não conseguir validar
      }

      // Força re-render dos campos quando a data de nascimento muda (apenas se mudou)
      const currentDob = formEdit.dob ? moment(formEdit.dob).format('YYYY-MM-DD') : '';
      if (newValue !== currentDob) {
        setTimeout(() => setFieldsKey(prev => prev + 1), 100);
      }
    }

    setFormEdit((prevValues) => ({
      ...prevValues,
      [fieldName]: newValue === false ? value : newValue,
    }));
  }, [formEdit.dob]);  // Função para criar os campos dinamicamente baseado no estado atual
  const getFields = () => {
    const baseFields = [
      {
        name: 'name',
        id: 'name',
        label: 'Nome',
        type: 'text',
        onChange: (e) => handleFieldChange('name', e.target.value),
        colSize: 4,
        additionalProps: {},
        group: 'Geral',
      },
      {
        name: 'lastname',
        id: 'lastname',
        label: 'Sobrenome',
        type: 'text',
        onChange: (e) => handleFieldChange('lastname', e.target.value),
        colSize: 4,
        additionalProps: {},
        group: 'Geral',
      },
      {
        name: 'company_id',
        id: 'company_id',
        label: 'Empresa',
        type: 'select',
        options: companies,
        onChange: (e) => handleFieldChange('company_id', e.target.value),
        colSize: 4,
        additionalProps: {},
        group: 'Geral',
      },
      {
        name: 'type_person',
        id: 'type_person',
        label: 'Tipo de pessoa',
        type: 'select',
        options: [
          { value: '', label: '-- Selecione --' },
          { value: 'fisica', label: 'Física' },
          { value: 'juridica', label: 'Jurídica' },
        ],
        onChange: (e) => handleFieldChange('type_person', e.target.value),
        colSize: 4,
        additionalProps: {},
        group: 'Geral',
      },
      {
        name: 'cpf_cnpj',
        id: 'cpf_cnpj',
        label: 'CPF ou CNPJ',
        type: 'text',
        value: formEdit.cpf_cnpj || '',
        onChange: (e, values) => {
          const raw = e.target.value;
          const type = values.type_person || formEdit.type_person; // fallback
          const formatted = formatCpfCnpjLive(raw, type);
          setFormEdit((prev) => ({
            ...prev,
            cpf_cnpj: formatted,
          }));
        },
        colSize: 4,
        additionalProps: {},
        group: 'Geral',
      },
      {
        name: 'dob',
        id: 'dob',
        label: 'Data de nascimento',
        type: 'text',
        value: displayDob || '',
        onChange: (e) => {
          const input = e.target;
          const value = input.value;
          const cursorPosition = input.selectionStart;

          // Remove tudo que não seja número
          const numbersOnly = value.replace(/\D/g, '');

          // Limita a 8 dígitos (DDMMYYYY)
          if (numbersOnly.length > 8) {
            return;
          }

          // Aplica máscara baseado na quantidade de números
          let masked = '';
          for (let i = 0; i < numbersOnly.length; i++) {
            if (i === 2 || i === 4) {
              masked += '/';
            }
            masked += numbersOnly[i];
          }

          // Atualiza o valor exibido
          setDisplayDob(masked);

          // Posiciona o cursor corretamente após a atualização
          setTimeout(() => {
            let newPosition = cursorPosition;
            if (masked.length > value.length) {
              // Se uma barra foi adicionada, move o cursor para frente
              newPosition++;
            }
            input.setSelectionRange(newPosition, newPosition);
          }, 0);

          // Valida quando completo (10 caracteres: DD/MM/YYYY)
          if (masked.length === 10) {
            const [day, month, year] = masked.split('/');
            const dateString = `${year}-${month}-${day}`;
            const isValid = moment(dateString, 'YYYY-MM-DD', true).isValid();
            if (isValid) {
              // Salva a data no formato interno
              handleFieldChange('dob', dateString);
              setDisplayDob(masked);
            }
          } else if (masked.length === 0) {
            handleFieldChange('dob', '');
          }
        },
        onBlur: () => {
          // Ao perder o foco, se tivermos uma data interna, mostrar formatada
          if (formEdit.dob) {
            const d = moment(formEdit.dob, 'YYYY-MM-DD', true);
            if (d.isValid()) {
              setDisplayDob(d.format('DD/MM/YYYY'));
              return;
            }
          }

          // Se não houver data interna, tente validar o displayDob e salvar
          if (displayDob && displayDob.length === 10) {
            const [day, month, year] = displayDob.split('/');
            const dateString = `${year}-${month}-${day}`;
            if (moment(dateString, 'YYYY-MM-DD', true).isValid()) {
              handleFieldChange('dob', dateString);
              setDisplayDob(moment(dateString, 'YYYY-MM-DD', true).format('DD/MM/YYYY'));
            }
          }
        },
        onKeyDown: (e) => {
          // Permitir apenas números e teclas de controle
          const allowedKeys = [
            'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
            'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'
          ];

          const isNumber = e.key >= '0' && e.key <= '9';
          const isAllowed = allowedKeys.includes(e.key);
          const isCtrlShortcut = e.ctrlKey && ['a', 'c', 'v', 'x', 'z'].includes(e.key.toLowerCase());

          if (!isNumber && !isAllowed && !isCtrlShortcut) {
            e.preventDefault();
          }
        },
        onPaste: (e) => {
          e.preventDefault();
          const paste = (e.clipboardData || window.clipboardData).getData('text');
          const numbersOnly = paste.replace(/\D/g, '');

          if (numbersOnly.length <= 8) {
            let masked = '';
            for (let i = 0; i < numbersOnly.length; i++) {
              if (i === 2 || i === 4) {
                masked += '/';
              }
              masked += numbersOnly[i];
            }
            setDisplayDob(masked);
            if (masked.length === 10) {
              const [day, month, year] = masked.split('/');
              const dateString = `${year}-${month}-${day}`;
              if (moment(dateString, 'YYYY-MM-DD', true).isValid()) {
                handleFieldChange('dob', dateString);
              }
            }
          }
        },
        colSize: 4,
        additionalProps: {
          placeholder: 'DD/MM/AAAA', // Ensure placeholder explicitly shows the format
          maxLength: 10,
          autoComplete: 'off',
          inputMode: 'numeric'
        },
        group: 'Geral',
      },
      {
        name: 'notes',
        id: 'notes',
        label: 'Observações',
        type: 'textarea',
        onChange: (e) => handleFieldChange('notes', e.target.value),
        colSize: 12,
        additionalProps: {},
        group: 'Geral',
      },
      {
        name: 'email',
        id: 'email',
        label: 'E-mail',
        type: 'email',
        onChange: (e) => handleFieldChange('email', e.target.value),
        colSize: 4,
        additionalProps: {},
        group: 'Contato',
      },
      {
        name: 'landline_phone',
        id: 'landline_phone',
        label: 'Telefone residêncial',
        type: 'number',
        onChange: (e) => handleFieldChange('landline_phone', e.target.value),
        colSize: 4,
        additionalProps: {},
        group: 'Contato',
      },
      {
        name: 'business_phone',
        id: 'business_phone',
        label: 'Telefone comercial',
        type: 'number',
        onChange: (e) => handleFieldChange('business_phone', e.target.value),
        colSize: 4,
        additionalProps: {},
        group: 'Contato',
      },
      {
        name: 'mobile_phone',
        id: 'mobile_phone',
        label: 'Celular',
        type: 'number',
        onChange: (e) => handleFieldChange('mobile_phone', e.target.value),
        colSize: 4,
        additionalProps: {},
        group: 'Contato',
      },
      {
        name: 'additional_phone',
        id: 'additional_phone',
        label: 'Telefone adicional',
        type: 'number',
        onChange: (e) => handleFieldChange('additional_phone', e.target.value),
        colSize: 4,
        additionalProps: {},
        group: 'Contato',
      },
    ];

    // Adiciona o campo "Nome do Responsável" se for menor de idade
    if (isMinor(formEdit.dob)) {
      const dobFieldIndex = baseFields.findIndex(field => field.name === 'dob');
      if (dobFieldIndex !== -1) {
        baseFields.splice(dobFieldIndex + 1, 0, {
          name: 'guardian_name',
          id: 'guardian_name',
          label: 'Nome do Responsável',
          type: 'text',
          value: formEdit.guardian_name || '',
          onChange: (e) => handleFieldChange('guardian_name', e.target.value),
          colSize: 4,
          additionalProps: {},
          group: 'Geral',
        });
      }
    }

    return baseFields;
  };

  const getCustomer = useCallback(
    async (updated = true) => {
      try {
        const customer = await axios.get(`${routeName}/${id}`);
        // Mantém a data interna em YYYY-MM-DD e preenche displayDob como DD/MM/YYYY
        let internalDob = '';
        let shownDob = '';
        if (customer.data.dob) {
          const date = moment.utc(customer.data.dob);
          if (date.isValid()) {
            internalDob = date.format('YYYY-MM-DD');
            shownDob = date.format('DD/MM/YYYY');
          }
        }

        setFormEdit({
          ...customer.data,
          dob: internalDob,
        });
        setDisplayDob(shownDob);
      } catch (err) {
        setApiErrors(err);
      } finally {
        setCustomerUpdated(updated);
      }
    },
    [id]
  );

  const getData = useCallback(async () => {
    try {
      setIsLoading(true);

      const optionsCompanies = [{ value: '', label: '-- Selecione --' }];
      const getCompanies = await axios.get(`/companies`);

      if (getCompanies) {
        getCompanies.data.forEach((data) => {
          optionsCompanies.push({
            value: data.id,
            label: data.name,
          });
        });
      }

      setCompanies(optionsCompanies);
      if (id) {
        await getCustomer();
      }
    } catch (err) {
      setApiErrors(err);
    } finally {
      setIsLoading(false);
      setDataLoaded(true);
    }
  }, [id]);

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      let action = 'cadastrado';
      let dataCustomer = {};

      // Faz uma cópia do formEdit e limpa os campos necessários
      const sanitizedForm = {
        ...formEdit,
        cpf_cnpj: formEdit.cpf_cnpj.replace(/\D/g, '')
      };

      // Se não tivermos a data interna mas tivermos displayDob, converte para o formato interno
      if ((!sanitizedForm.dob || sanitizedForm.dob === '') && displayDob) {
        if (displayDob.includes('/')) {
          const [day, month, year] = displayDob.split('/');
          sanitizedForm.dob = `${year}-${month}-${day}`;
        }
      }

      // Converte a data para o formato YYYY-MM-DD se estiver no formato DD/MM/YYYY
      if (sanitizedForm.dob && sanitizedForm.dob.includes('/')) {
        const [day, month, year] = sanitizedForm.dob.split('/');
        sanitizedForm.dob = `${year}-${month}-${day}`;
      }

      console.log(sanitizedForm.cpf_cnpj)

      if (id) {

        await axios.put(`${routeName}/${id}`, sanitizedForm);
        action = 'atualizado';
      } else {
        console.log(sanitizedForm)
        dataCustomer = await axios.post(routeName, sanitizedForm);
      }

      notification(`${dataName} ${action} com sucesso`, 'success');
      if (!id) {
        setId(dataCustomer.data.id);
        navigate(`${routeName}/${dataCustomer.data.id}/edit`);
      } else {
        await getCustomer();
      }
    } catch (err) {
      setApiErrors(err);
    } finally {
      setIsLoading(false);
    }
  };

  const alterStatusCustomer = useCallback(async () => {
    try {
      setIsLoading(true);
      if (formEdit.status === 1 && !formEdit.reason_deactivation) {
        notification('Você deve informar o motivo de desativação do cliente!');
        return;
      }

      const customer = await axios.put(`${routeName}/${id}/tooglestatus`, {
        reason_deactivation: formEdit.reason_deactivation,
      });

      await getCustomer();

      notification(
        `Cliente ${customer.data.status === 0 ? 'desativado' : 'reativado'} com sucesso`,
        'success'
      );
    } catch (err) {
      setApiErrors(err);
    } finally {
      setIsLoading(false);
    }
  }, [formEdit]);

  useEffect(() => {
    getData();
  }, [id]);

  useEffect(() => {
    if (customerUpdated === true) {
      getCustomer(false);
    }
  }, [customerUpdated]);

  return (
    <Container fluid className="px-lg-4 px-xl-5">
      <Loading isLoading={isLoading} />
      <Breadcrumbs
        title={id ? 'Atualizar' : 'Cadastrar'}
        pages={[{ link: routeName, name: titleName }]}
      />
      <div className="page-header d-flex justify-content-between align-items-center">
        <h1 className="page-heading">{titleName}</h1>
        {formEdit.id && dataLoaded ? (
          <div>
            <ButtonModal
              onConfirm={() => alterStatusCustomer()}
              message={
                <>
                  <b>
                    Confirmar
                    {formEdit.status === 1 ? ' desativação' : ' reativação'} do
                    cliente {formEdit.name} ?
                  </b>
                  {formEdit.status === 1 && (
                    <div className="mt-3">
                      <Form.Control
                        as="textarea"
                        className="form-textarea"
                        name="reason_deactivation"
                        value={formEdit.reason_deactivation}
                        onChange={(e) =>
                          handleFieldChange(
                            'reason_deactivation',
                            e.target.value
                          )
                        }
                      />
                      <Form.Label className="text-sm">
                        Informe o motivo da desativação do cliente
                      </Form.Label>
                    </div>
                  )}
                </>
              }
              buttonProps={{
                variant: formEdit.status === 1 ? 'danger' : 'success',
                className: 'text-uppercase text-white',
                title: `Clique para ${formEdit.status === 1 ? 'desativar' : 'reativar'} o cliente`,
              }}
              textModal={{
                title: `${formEdit.status === 1 ? 'Desativar' : 'Reativar'} cliente`,
                textConfirm: `Confirmar`,
              }}
            >
              {formEdit.status === 1 ? 'Desativar' : 'Reativar'}
            </ButtonModal>
          </div>
        ) : null}
      </div>
      <section>
        <Row>
          {formEdit.id && dataLoaded ? (
            <Col lg={4}>
              <>
                <Card className="card-profile mb-4">
                  <Card.Header
                    className="overflow-hidden"
                    style={{
                      backgroundImage: `url(/img/photos/greenShapes.jpg)`,
                    }}
                  />
                  <Card.Body className="text-center">
                    <div
                      className="card-profile-img mx-auto d-flex align-center"
                      style={{ maxHeight: '8rem' }}
                    >
                      <div className="position-relative overflow-hidden rounded-circle">
                        <CustomImage
                          src={formEdit.photo || '/img/user-profile-circle.png'}
                          layout="fixed"
                          width={122}
                          height={122}
                          priority
                        />
                      </div>
                    </div>

                    <h3 className="mb-3">
                      {formEdit.name} {formEdit.lastname || ''}
                    </h3>
                    <p className="mb-4">{formEdit.cpf_cnpj}</p>
                  </Card.Body>
                </Card>
                <Card className="mb-4">
                  <Card.Header>
                    <h4 className="card-heading">Informações de cadastro</h4>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col>
                        <Form.Label>Situação do cliente</Form.Label>
                        <h6
                          className={
                            formEdit.status === 1
                              ? 'text-success'
                              : 'text-danger'
                          }
                        >
                          {formEdit.status === 1 ? 'Ativo' : 'Inativo'}
                        </h6>
                      </Col>
                    </Row>
                    <hr className="bg-gray-400 my-4" />
                    {formEdit.CustomerContracts &&
                      formEdit.CustomerContracts.length > 0 ? (
                      <>
                        <Row>
                          <Col>
                            <Form.Label>Situação contratual</Form.Label>
                            <h6
                              className={
                                formEdit.CustomerContracts[0].status === 1
                                  ? 'text-success'
                                  : 'text-danger'
                              }
                            >
                              {formEdit.CustomerContracts[0].status === 1
                                ? 'Adimplente'
                                : 'Inadimplente'}
                            </h6>
                          </Col>
                        </Row>
                        <hr className="bg-gray-400 my-4" />
                      </>
                    ) : null}
                    {formEdit.status === 0 && (
                      <>
                        <Row>
                          <Col>
                            <Form.Label>
                              Motivo de desativação do cliente
                            </Form.Label>
                            <h6>{formEdit.reason_deactivation}</h6>
                          </Col>
                        </Row>
                        <hr className="bg-gray-400 my-4" />
                      </>
                    )}
                    <Row>
                      <Col>
                        <Form.Label>Empresa</Form.Label>
                        <h6>
                          {formEdit?.Company?.name || 'Empresa não informada'}
                        </h6>
                      </Col>
                    </Row>
                    <hr className="bg-gray-400 my-4" />
                    <Row>
                      <Col>
                        <Form.Label>Idade</Form.Label>
                        <h6>
                          {formEdit.age || formEdit.age === 0
                            ? formEdit.age
                            : 'Não informado'}
                        </h6>
                      </Col>
                    </Row>
                    <hr className="bg-gray-400 my-4" />
                    <Row>
                      <Col>
                        <Form.Label>Data de cadastro</Form.Label>
                        <h6>
                          {moment(formEdit.created_at).format(
                            'DD/MM/YYYY [às] HH:mm'
                          )}
                        </h6>
                      </Col>
                    </Row>
                    <hr className="bg-gray-400 my-4" />
                    <Row>
                      <Col>
                        <Form.Label>Data última atualização</Form.Label>
                        <h6>
                          {moment(formEdit.updated_at).format(
                            'DD/MM/YYYY [às] HH:mm'
                          )}
                        </h6>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </>
            </Col>
          ) : null}
          <Col lg={id ? 8 : 12}>
            <Tabs
              activeKey={activeTab}
              onSelect={(eventKey) => setActiveTab(eventKey)}
              className="mb-5"
            >
              <Tab eventKey="main" title="Pessoal">
                {!id || dataLoaded ? (
                  <Card className="mb-5">
                    <Card.Header>
                      <h4 className="card-heading">
                        {id
                          ? `Atualizar cadastro do ${dataName}`
                          : `Cadastrar ${dataName}`}
                      </h4>
                    </Card.Header>
                    <Card.Body>
                      <ErrorProcessor error={apiErrors} />
                      {dataLoaded ? (
                        <Forms
                          key={fieldsKey}
                          initialValues={formEdit}
                          validationSchema={validationSchema}
                          onSubmit={handleSubmit}
                          fields={getFields()}
                          btnSubmit={{
                            variant: 'primary',
                            type: 'submit',
                            text: id ? 'Atualizar' : 'Cadastrar',
                          }}
                        />
                      ) : null}
                    </Card.Body>
                  </Card>
                ) : null}
              </Tab>
              {dataLoaded && formEdit.id ? (
                <Tab eventKey="address" title="Endereço">
                  <CustomerAddr customer={formEdit} />
                </Tab>
              ) : null}
              {dataLoaded && formEdit.id ? (
                <Tab eventKey="payment" title="Dados bancários">
                  <CustomerPay customer={formEdit} />
                </Tab>
              ) : null}
              {dataLoaded && formEdit.id ? (
                <Tab eventKey="contract" title="Contrato">
                  <CustomerContract
                    customer={formEdit}
                    stateAction={setCustomerUpdated}
                    stateInfo={customerUpdated}
                  />
                </Tab>
              ) : null}
              {dataLoaded && formEdit.id ? (
                <Tab eventKey="paymentMethod" title="Pagamentos">
                  <CustomerPayMethod
                    customer={formEdit}
                    stateAction={setCustomerUpdated}
                    stateInfo={customerUpdated}
                  />
                </Tab>
              ) : null}
            </Tabs>
          </Col>
        </Row>
      </section>
    </Container>
  );
}
